import { useEffect, useMemo } from "react";
import useUserStore from "@/stores/user.store";
import useUserTokenStore from "@/stores/user-token.store";
import { parseTenantIdFromAccessToken } from "@/utils/auth";
import {
  maintainDashboardSkeletonCache,
  readDashboardSkeletonLayout,
  type DashboardSkeletonLayoutWidget,
} from "@/lib/dashboard-skeleton-cache";

/** Cached widget grid for the dashboard loading skeleton (same user + tenant only). */
export function useCachedDashboardSkeletonLayout(
  selectedDashboardId: string | null
): DashboardSkeletonLayoutWidget[] | null {
  const me = useUserStore((s) => s.me);
  const token = useUserTokenStore((s) => s.token);
  const tenantId = useMemo(
    () => parseTenantIdFromAccessToken(token),
    [token]
  );

  const layout = useMemo(() => {
    if (!me?.id || !tenantId) return null;
    return readDashboardSkeletonLayout(selectedDashboardId, {
      userId: me.id,
      tenantId,
    });
  }, [me?.id, selectedDashboardId, tenantId]);

  useEffect(() => {
    if (!me?.id || !tenantId) return;
    maintainDashboardSkeletonCache({ userId: me.id, tenantId });
  }, [me?.id, tenantId]);

  return layout;
}
